import { useEffect, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import {
  BookingFlowShell,
  type BookingFlowShellConfig,
} from '../../../booking-flow/BookingFlowShell';
import { busFlowConfig, type BusFlowState, type BusTripType } from '../flowConfig';
import { busResumeAdapter } from '../resume';
import { BusLocationAutocomplete } from '../components/BusLocationAutocomplete';
import { SeatPicker } from '../components/SeatPicker';
import { busCompanyNameFromLogo, busVehicleLabel, busVehiclePrice } from '../tripFields';
import { PaymentForm } from '../../../components/payment/PaymentForm';
import { PersonnelAutocomplete } from '../../../components/PersonnelAutocomplete';
import { PhoneField } from '../../../components/PhoneField';
import {
  BillingAccountCard,
  emptyBillingAccountValue,
  type BillingAccountValue,
} from '../../../components/booking/BillingAccountCard';
import { buildBillingInformation } from '../../../components/booking/billingInfo';
import { OutOfBudgetText } from '../../../components/booking/OutOfBudgetChip';
import { BookingConfirmation } from '../../../components/booking/BookingConfirmation';
import { ApprovalPassengerStep } from '../../../components/booking/ApprovalPassengerStep';
import {
  approvalEmployeeId,
  corporateMaxBudget,
  needsCorporateApproval,
} from '../../../components/booking/corporateApproval';
import { SendForApprovalPanel } from '../../../components/booking/SendForApprovalPanel';
import type { BusSeat, BusVehicle } from '../../../api/types/bus';
import type { CorporateEmployee } from '../../../api/types/corporateEmployee';

/* ---------------------------------------------------------------------------------- *
 * Step 0 — Sefer arama
 * ---------------------------------------------------------------------------------- */
function SearchStep() {
  const flow = useBookingFlowContext<BusFlowState>();
  const [tripType, setTripType] = useState<BusTripType>(
    flow.state.searchForm.tripType ?? (flow.state.searchForm.retDate ? 'RoundTrip' : 'OneWay')
  );
  const [origin, setOrigin] = useState(flow.state.searchForm.origin ?? null);
  const [destination, setDestination] = useState(flow.state.searchForm.destination ?? null);
  const [depDate, setDepDate] = useState<Dayjs | null>(
    flow.state.searchForm.depDate ? dayjs(flow.state.searchForm.depDate) : dayjs()
  );
  const [retDate, setRetDate] = useState<Dayjs | null>(
    flow.state.searchForm.retDate ? dayjs(flow.state.searchForm.retDate) : null
  );
  const [passengerCount, setPassengerCount] = useState(flow.state.searchForm.passengerCount || 1);
  const [employee, setEmployee] = useState<CorporateEmployee | null>(flow.state.searchForm.employee ?? null);

  const isRoundTrip = tripType === 'RoundTrip';

  const handleTripTypeChange = (next: BusTripType) => {
    setTripType(next);
    // Gidiş-dönüşe geçince dönüş tarihini gidişe eşitle (boş ya da gidişten önceyse).
    if (next === 'RoundTrip' && (!retDate || (depDate && retDate.isBefore(depDate, 'day')))) {
      setRetDate(depDate ?? dayjs());
    }
  };

  const handleDepDateChange = (next: Dayjs | null) => {
    setDepDate(next);
    if (isRoundTrip && next && retDate && retDate.isBefore(next, 'day')) setRetDate(next);
  };

  const canSearch =
    !!origin && !!destination && !!depDate && (!isRoundTrip || (!!retDate && !retDate.isBefore(depDate, 'day')));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={tripType}
        onChange={(_, value) => value && handleTripTypeChange(value)}
      >
        <ToggleButton value="OneWay">Tek Yön</ToggleButton>
        <ToggleButton value="RoundTrip">Gidiş-Dönüş</ToggleButton>
      </ToggleButtonGroup>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <BusLocationAutocomplete label="Nereden" value={origin} onChange={setOrigin} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <BusLocationAutocomplete label="Nereye" value={destination} onChange={setDestination} />
        </Box>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <DatePicker
          label="Gidiş tarihi"
          value={depDate}
          onChange={handleDepDateChange}
          minDate={dayjs()}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
        {isRoundTrip && (
          <DatePicker
            label="Dönüş tarihi"
            value={retDate}
            onChange={setRetDate}
            minDate={depDate ?? dayjs()}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        )}
        <TextField
          label="Yolcu sayısı"
          type="number"
          size="small"
          value={passengerCount}
          onChange={(e) => setPassengerCount(Math.max(1, Number(e.target.value) || 1))}
          slotProps={{ htmlInput: { min: 1, max: 9 } }}
          sx={{ minWidth: 140 }}
        />
      </Stack>
      <PersonnelAutocomplete value={employee} onChange={setEmployee} />
      <Button
        variant="contained"
        disabled={!canSearch || flow.status === 'running'}
        onClick={() =>
          flow.runStep({
            searchForm: {
              origin: origin ?? undefined,
              destination: destination ?? undefined,
              tripType,
              depDate: depDate ? depDate.format('YYYY-MM-DD') : '',
              retDate: isRoundTrip && retDate ? retDate.format('YYYY-MM-DD') : undefined,
              passengerCount,
              employee: employee ?? undefined,
            },
          })
        }
        sx={{ alignSelf: 'flex-start' }}
      >
        Sefer Ara
      </Button>
    </Box>
  );
}

/* ---------------------------------------------------------------------------------- *
 * Step 1 — Sefer seçimi (Bus/Search sonuçları)
 * ---------------------------------------------------------------------------------- */
function TripSelectionStep() {
  const flow = useBookingFlowContext<BusFlowState>();
  const vehicles = flow.state.searchResult?.vehicles ?? [];
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const running = flow.status === 'running';

  // Bir sefer seçilince hemen `Bus/GetBusSchema`'ya gidilir (config step 'schema') ve
  // koltuk seçimi adımına geçilir — ayrı "Devam" tıklaması yok.
  const pickTrip = (trip: BusVehicle, index: number) => {
    if (running) return;
    setPendingIndex(index);
    flow.runStep({ selectedTrip: trip }).catch(() => setPendingIndex(null));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {vehicles.length === 0 && <Alert severity="info">Bu kriterlere uygun sefer bulunamadı.</Alert>}
      {vehicles.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Bir sefer seçtiğinizde koltuk planı yüklenir.
        </Typography>
      )}
      <Stack spacing={1}>
        {vehicles.map((trip, index) => {
          const isPending = pendingIndex === index && running;
          return (
            <Card
              key={index}
              variant="outlined"
              sx={{
                cursor: running ? 'default' : 'pointer',
                opacity: running && !isPending ? 0.55 : 1,
                borderColor: isPending ? 'primary.main' : undefined,
                borderWidth: isPending ? 2 : 1,
                '&:hover': running ? undefined : { borderColor: 'primary.main' },
              }}
              onClick={() => pickTrip(trip, index)}
            >
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
                  {trip.segmentLogoUrl && (
                    <Box
                      component="img"
                      src={trip.segmentLogoUrl}
                      alt={busCompanyNameFromLogo(trip.segmentLogoUrl)}
                      sx={{ width: 88, height: 64, objectFit: 'contain', flexShrink: 0 }}
                    />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {busVehicleLabel(trip)}
                    </Typography>
                    {typeof trip.genEmptySeat === 'number' && (
                      <Typography variant="caption" color="text.secondary">
                        {Math.trunc(trip.genEmptySeat)} boş koltuk
                        {trip.provider ? ` · ${trip.provider}` : ''}
                      </Typography>
                    )}
                    {trip.isOutBudget && (
                      <Box sx={{ mt: 0.5 }}>
                        <OutOfBudgetText maxBudget={trip.maxBudget} />
                      </Box>
                    )}
                  </Box>
                </Stack>
                {isPending ? (
                  <CircularProgress size={20} sx={{ flexShrink: 0 }} />
                ) : (
                  busVehiclePrice(trip) !== undefined && (
                    <Chip
                      size="small"
                      color={trip.isOutBudget ? 'warning' : 'default'}
                      label={`${busVehiclePrice(trip)} ${trip.currency ?? 'TRY'}`}
                      sx={{ flexShrink: 0 }}
                    />
                  )
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

/* ---------------------------------------------------------------------------------- *
 * Step 2 — Koltuk seçimi (Bus/GetBusSchema)
 * ---------------------------------------------------------------------------------- */
const seatId = (s: BusSeat): string | number => s.seatNum ?? s.seatNumber ?? s.seatNo ?? '';

function SeatSelectionStep() {
  const flow = useBookingFlowContext<BusFlowState>();
  const [selectedSeats, setSelectedSeats] = useState<BusSeat[]>(flow.state.selectedSeats);
  const [manualSeat, setManualSeat] = useState('');
  const [manualGender, setManualGender] = useState<'E' | 'K'>('E');

  const seatCount = Math.max(flow.state.searchForm.passengerCount || 1, 1);
  const structured = !!flow.state.seatSchema?.seats?.length;

  const selectSeat = (seat: BusSeat, gender: 'E' | 'K') => {
    setSelectedSeats((prev) => {
      if (prev.some((s) => seatId(s) === seatId(seat))) return prev;
      if (prev.length >= seatCount) return prev;
      return [...prev, { ...seat, selectedGender: gender }];
    });
  };
  const removeSeat = (seat: BusSeat) =>
    setSelectedSeats((prev) => prev.filter((s) => seatId(s) !== seatId(seat)));

  // Otobüste `seatPrice` genelde koltuğun tam ücretidir (ayrı bir sefer fiyatı yok).
  const seatsTotal = selectedSeats.reduce((sum, s) => sum + (s.seatPrice ?? 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {seatCount} koltuk seçin. Boş koltuğa tıklayıp cinsiyet belirtin — yan koltuk dolu ise
        yalnız aynı cinsiyet seçilebilir.
      </Typography>

      <SeatPicker
        seats={flow.state.seatSchema?.seats}
        informationMessage={flow.state.seatSchema?.informationMessage}
        busSchemaHtml={flow.state.seatSchema?.busSchemaHtml}
        selectedSeats={selectedSeats}
        onSelect={selectSeat}
        onRemove={removeSeat}
        maxSeats={seatCount}
      />

      {!structured && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TextField
            size="small"
            label="Koltuk no"
            value={manualSeat}
            onChange={(e) => setManualSeat(e.target.value)}
          />
          <TextField
            select
            size="small"
            label="Cinsiyet"
            value={manualGender}
            onChange={(e) => setManualGender(e.target.value as 'E' | 'K')}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="E">Erkek</MenuItem>
            <MenuItem value="K">Kadın</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            disabled={!manualSeat || selectedSeats.length >= seatCount}
            onClick={() => {
              selectSeat({ seatNo: manualSeat, seatNum: Number(manualSeat) || undefined }, manualGender);
              setManualSeat('');
            }}
          >
            Ekle
          </Button>
        </Stack>
      )}

      {selectedSeats.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {selectedSeats.map((s) => (
            <Chip
              key={seatId(s)}
              size="small"
              color={s.selectedGender === 'K' ? 'secondary' : 'primary'}
              label={`${s.seatNum ?? s.seatNo} · ${s.selectedGender === 'K' ? 'Kadın' : 'Erkek'}`}
              onDelete={() => removeSeat(s)}
            />
          ))}
        </Stack>
      )}

      <Typography variant="body2" color="text.secondary">
        Seçili: {selectedSeats.length}/{seatCount}
        {seatsTotal > 0 ? ` · Tutar: ${seatsTotal.toLocaleString('tr-TR')} TL` : ''}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          disabled={flow.status === 'running'}
          onClick={() => {
            setSelectedSeats([]);
            flow.patchState({ selectedSeats: [], seatSchema: undefined });
            flow.goToStep(1);
          }}
        >
          Sefer Seçimine Dön
        </Button>
        <Button
          variant="contained"
          disabled={selectedSeats.length !== seatCount || flow.status === 'running'}
          onClick={() => flow.runStep({ selectedSeats })}
        >
          Devam (Rezervasyona Al)
        </Button>
      </Stack>
    </Box>
  );
}

/* ---------------------------------------------------------------------------------- *
 * Step 3 — Yolcu bilgileri (Bus/UpdatePassengers) — react-hook-form + zod
 * ---------------------------------------------------------------------------------- */
const passengerSchema = z.object({
  firstName: z.string().min(1, 'Ad zorunludur'),
  lastName: z.string().min(1, 'Soyad zorunludur'),
  citizenNo: z.string().regex(/^\d{11}$/, '11 haneli T.C. Kimlik No girin'),
  gender: z.string().min(1, 'Cinsiyet seçiniz'),
  birthDate: z.string().min(1, 'Doğum tarihi zorunludur'),
});

const passengersFormSchema = z.object({
  passengers: z.array(passengerSchema).min(1, 'En az bir yolcu gereklidir'),
  contactName: z.string().min(1, 'Ad zorunludur'),
  contactLastName: z.string().min(1, 'Soyad zorunludur'),
  contactPhone: z.string().min(1, 'Telefon zorunludur'),
  contactEmail: z.string().email('Geçerli bir e-posta giriniz'),
});

type PassengersFormValues = z.infer<typeof passengersFormSchema>;

/** Koltuk seçiminde belirlenen cinsiyet ('E'/'K') → yolcu formundaki select değeri. */
const seatGenderToPax = (sg: 'E' | 'K' | undefined): string =>
  sg === 'K' ? 'Female' : sg === 'E' ? 'Male' : '';

function PassengersStep() {
  const flow = useBookingFlowContext<BusFlowState>();
  const passengerCount = Math.max(flow.state.selectedSeats.length, flow.state.searchForm.passengerCount, 1);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PassengersFormValues>({
    resolver: zodResolver(passengersFormSchema),
    defaultValues: {
      // Cinsiyet koltuk seçiminden ön-doldurulur (i. yolcu ↔ i. koltuk); kullanıcı değiştirebilir.
      passengers: Array.from({ length: passengerCount }, (_, i) => {
        const p = flow.state.passengers[i];
        return {
          firstName: p?.firstName ?? '',
          lastName: p?.lastName ?? '',
          citizenNo: p?.citizenNo ?? '',
          gender: p?.gender || seatGenderToPax(flow.state.selectedSeats[i]?.selectedGender),
          birthDate: p?.birthDate ?? '',
        };
      }),
      contactName: flow.state.contact?.name ?? '',
      contactLastName: flow.state.contact?.lastName ?? '',
      contactPhone: flow.state.contact?.phone ?? '',
      contactEmail: flow.state.contact?.email ?? '',
    },
  });

  const { fields } = useFieldArray({ control, name: 'passengers' });

  const [billing, setBilling] = useState<BillingAccountValue>(() => ({
    ...emptyBillingAccountValue,
    currentAccount: flow.state.selectedCurrentAccount ?? null,
  }));

  const onSubmit = (values: PassengersFormValues) => {
    flow.runStep({
      passengers: values.passengers,
      contact: {
        name: values.contactName,
        lastName: values.contactLastName,
        phone: values.contactPhone,
        email: values.contactEmail,
      },
      selectedCurrentAccount: billing.currentAccount ?? undefined,
      billingInformation: buildBillingInformation(
        billing.currentAccount,
        billing.billingName || `${values.contactName} ${values.contactLastName}`.trim(),
        billing.identityOrTaxNo || undefined
      ),
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {fields.map((field, index) => {
        const seat = flow.state.selectedSeats[index];
        return (
        <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Yolcu {index + 1}
            {seat?.seatNum != null || seat?.seatNo
              ? ` · Koltuk ${seat.seatNum ?? seat.seatNo}${
                  seat.selectedGender ? ` (${seat.selectedGender === 'K' ? 'Kadın' : 'Erkek'})` : ''
                }`
              : ''}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
            <Controller
              name={`passengers.${index}.firstName`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  label="Ad"
                  size="small"
                  fullWidth
                  error={!!errors.passengers?.[index]?.firstName}
                  helperText={errors.passengers?.[index]?.firstName?.message}
                />
              )}
            />
            <Controller
              name={`passengers.${index}.lastName`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  label="Soyad"
                  size="small"
                  fullWidth
                  error={!!errors.passengers?.[index]?.lastName}
                  helperText={errors.passengers?.[index]?.lastName?.message}
                />
              )}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
            <Controller
              name={`passengers.${index}.citizenNo`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  label="T.C. Kimlik No"
                  size="small"
                  fullWidth
                  slotProps={{ htmlInput: { maxLength: 11, inputMode: 'numeric' } }}
                  error={!!errors.passengers?.[index]?.citizenNo}
                  helperText={errors.passengers?.[index]?.citizenNo?.message}
                />
              )}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name={`passengers.${index}.gender`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  select
                  label="Cinsiyet"
                  size="small"
                  fullWidth
                  error={!!errors.passengers?.[index]?.gender}
                  helperText={errors.passengers?.[index]?.gender?.message}
                >
                  <MenuItem value="Male">Erkek</MenuItem>
                  <MenuItem value="Female">Kadın</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name={`passengers.${index}.birthDate`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  type="date"
                  label="Doğum tarihi"
                  size="small"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.passengers?.[index]?.birthDate}
                  helperText={errors.passengers?.[index]?.birthDate?.message}
                />
              )}
            />
          </Stack>
        </Paper>
        );
      })}

      <Divider />
      <Typography variant="subtitle2">İletişim bilgileri</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="contactName"
          control={control}
          render={({ field: f }) => (
            <TextField {...f} label="Ad" size="small" fullWidth error={!!errors.contactName} helperText={errors.contactName?.message} />
          )}
        />
        <Controller
          name="contactLastName"
          control={control}
          render={({ field: f }) => (
            <TextField
              {...f}
              label="Soyad"
              size="small"
              fullWidth
              error={!!errors.contactLastName}
              helperText={errors.contactLastName?.message}
            />
          )}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="contactPhone"
          control={control}
          render={({ field: f }) => (
            <PhoneField
              value={f.value}
              onChange={f.onChange}
              onBlur={f.onBlur}
              label="Telefon"
              error={!!errors.contactPhone}
              helperText={errors.contactPhone?.message}
            />
          )}
        />
        <Controller
          name="contactEmail"
          control={control}
          render={({ field: f }) => (
            <TextField
              {...f}
              label="E-posta"
              size="small"
              fullWidth
              error={!!errors.contactEmail}
              helperText={errors.contactEmail?.message}
            />
          )}
        />
      </Stack>

      <Divider />
      <BillingAccountCard value={billing} onChange={setBilling} />

      <Button type="submit" variant="contained" disabled={flow.status === 'running'} sx={{ alignSelf: 'flex-start' }}>
        Devam (Yolcuları Kaydet)
      </Button>
    </Box>
  );
}

/* ---------------------------------------------------------------------------------- *
 * Step 5 — Kesinleştirme (yalnız ödeme sonrası inline finalize başarısız kalırsa)
 * ---------------------------------------------------------------------------------- */
function BusFinalizeStep() {
  const flow = useBookingFlowContext<BusFlowState>();
  const finalized = !!flow.state.shoppingCart?.isFinalized;

  // Ödeme adımı FinalizeShopping'i zaten zincirledi — biletlendiyse doğrudan belgeye geç.
  useEffect(() => {
    if (finalized && flow.status === 'idle') flow.goToStep(6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalized, flow.status]);

  if (finalized) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
      <Alert severity="warning">
        Ödemeniz alındı ancak biletleme (PNR) tamamlanamadı. Tekrar denemek için Kesinleştir'e basın.
      </Alert>
      <Button
        variant="contained"
        disabled={flow.status === 'running'}
        onClick={() => flow.runStep().catch(() => undefined)}
        sx={{ alignSelf: 'flex-start' }}
      >
        {flow.status === 'running' ? 'İşleniyor…' : 'Kesinleştir'}
      </Button>
    </Box>
  );
}

/* ---------------------------------------------------------------------------------- *
 * Step 4 — Ödeme (MakePayment; başarılıysa FinalizeShopping zincirlenir)
 * ---------------------------------------------------------------------------------- */
function PaymentStep() {
  const flow = useBookingFlowContext<BusFlowState>();
  return (
    <Box sx={{ maxWidth: 520 }}>
      <SendForApprovalPanel product="Bus" cart={flow.state.shoppingCart} />
      <PaymentForm
        amount={flow.state.shoppingCart?.remainingTotal}
        currency="TRY"
        loading={flow.status === 'running'}
        shoppingCartId={flow.state.shoppingCartId}
        currentAccount={flow.state.selectedCurrentAccount}
        onSubmit={({ paymentForm, paymentType }) => {
          flow.runStep({ paymentForm, paymentType }).catch(() => undefined);
        }}
      />
    </Box>
  );
}

/* ---------------------------------------------------------------------------------- *
 * Step 6 — Belge (PNR / özet)
 * ---------------------------------------------------------------------------------- */
function DocumentStep() {
  const flow = useBookingFlowContext<BusFlowState>();
  const booking = flow.state.shoppingCart?.busBookings?.[0] as
    | { pnr?: string; referenceNumber?: string; status?: string }
    | undefined;
  const continueUrl = flow.state.payment?.continueUrl;
  const isFinalized = !!flow.state.shoppingCart?.isFinalized;

  const references = [
    booking?.pnr ? { label: 'PNR', value: booking.pnr } : null,
    booking?.referenceNumber ? { label: 'Referans No', value: booking.referenceNumber } : null,
  ].filter((r): r is { label: string; value: string } => r !== null);

  return (
    <BookingConfirmation
      status={isFinalized ? 'success' : 'partial'}
      message={isFinalized ? 'Rezervasyon tamamlandı.' : 'Rezervasyon süreci sonlandırıldı.'}
      references={references}
      summary={
        <Stack spacing={1}>
          {booking?.status && (
            <Chip size="small" label={`Durum: ${booking.status}`} sx={{ alignSelf: 'flex-start' }} />
          )}
          {continueUrl && (
            <Alert severity="warning">
              Ödeme 3D Secure onayı gerektiriyor.{' '}
              <a href={continueUrl} target="_blank" rel="noreferrer">
                Devam etmek için tıklayın
              </a>
              .
            </Alert>
          )}
        </Stack>
      }
      primaryAction={{ label: 'Baştan başla', onClick: flow.reset }}
    />
  );
}

/* ---------------------------------------------------------------------------------- *
 * Flow shell
 * ---------------------------------------------------------------------------------- */
function busApprovalActive(state: BusFlowState, sessionEmployeeId: number | null) {
  const gate = {
    cart: state.shoppingCart,
    searchEmployeeId: state.searchForm.employee?.employeeID,
    extraOverBudget: state.selectedTrip?.isOutBudget,
    extraMaxBudget: state.selectedTrip?.maxBudget,
  };
  const employeeId = approvalEmployeeId(gate, sessionEmployeeId);
  const paxCount = Math.max(state.selectedSeats.length, state.searchForm.passengerCount, 1);
  if (paxCount !== 1 || !needsCorporateApproval(gate) || !employeeId) return null;
  return { employeeId, maxBudget: corporateMaxBudget(gate) };
}

const shellConfig: BookingFlowShellConfig<BusFlowState> = {
  title: 'Otobüs Bileti',
  description:
    'Sefer arama, koltuk seçimi, yolcu bilgileri ve ödeme adımlarını tek akışta tamamlayın.',
  flowConfig: busFlowConfig,
  resumeAdapter: busResumeAdapter,
  sentForApproval: {
    isSent: (s) => !!s.sentForApproval,
    passengerStepId: 'passengers',
    reservationPath: '/bus/reservations',
    message:
      'Otobüs bileti onaya gönderilmiştir. Yöneticiniz onayladığında rezervasyon listenizden ödemeye devam edebilirsiniz.',
  },
  approvalGate: {
    passengerStepId: 'passengers',
    isActive: busApprovalActive,
    render: ({ flow, running, employeeId, maxBudget }) => (
      <ApprovalPassengerStep
        employeeId={employeeId}
        maxBudget={maxBudget}
        productLabel="otobüs bileti"
        loading={running}
        onSubmit={(passengers, contact, billingInformation) => {
          flow
            .runStep({ passengers, contact, billingInformation, sentForApproval: true })
            .catch(() => undefined);
        }}
      />
    ),
  },
  renderStep: {
    search: () => <SearchStep />,
    schema: () => <TripSelectionStep />,
    allocate: () => <SeatSelectionStep />,
    passengers: () => <PassengersStep />,
    payment: () => <PaymentStep />,
    finalize: () => <BusFinalizeStep />,
    document: () => <DocumentStep />,
  },
};

export default function BusFlowPage() {
  return <BookingFlowShell config={shellConfig} />;
}
